import { useFormBehaviors, useURLParameters } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { METHOD_PATCH } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { B2B_JANES } from 'constants/endpoints';
import { toastService } from 'services';
import {
  DropdownItem,
  getPositionByDepartmentSelectOptions,
  GroupedPositionByDepartmentSelectOptionType,
  mapPositionIdsByDepartment,
} from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.constants';
import { janeDetailsModalRoutingService } from 'modals/JaneDetailsModal/JaneDetailsModal.constants';
import { useFetchJaneByIdDataContext } from 'modals/JaneDetailsModal/FetchJaneById.data.context';
import { SelectInputPositionByDepartmentContext } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.context';
import { SelectPositionByDepartmentDataType } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.types';
import i18next from 'i18next';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { ChangeEventType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { useAuthRouteDataContext } from 'providers/AuthRouteDataProvider/AuthRouteDataProvider.context';
import { removePositionModalRoutingService } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { useJanePositionDropdownHandlers } from 'hooks/useJanePositionDropdownHandlers';
import {
  prepareEditPayload,
  VALIDATION_position_id,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/views/JaneModal/EditJaneView/EditJaneView.constants';
import { IBKJComboBoxAsyncOption } from '@bookjane2/bookjane-design-library/lib/components/BKJComboBoxAsync/BKJComboBoxAsync.types';
import { usePositionsAllocationSettingsPageDataContext } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocationSettingsPage.data.context';
export interface EditJaneApiError {
  data: {
    error_messages: {
      phone?: string[];
    };
  };
}
export interface IEditJaneViewForm {
  location: IBKJComboBoxAsyncOption;
  position_id: string[];
  selectedSecondaryPositions: string[];
}

type SecondaryPositionDropdownItem = DropdownItem & GroupedPositionByDepartmentSelectOptionType;

export interface IEditJaneViewFormActions {
  secondaryPositionsDropdownItems: SecondaryPositionDropdownItem[];
  showDeactivateConfirmModal: boolean;
  setShowDeactivateConfirmModal: (showDeactivateConfirmModal: boolean) => void;
  setIsDiscardConfirmationOpen: (isDiscardConfirmationOpen: boolean) => void;
  isDiscardConfirmationOpen: boolean;
  handleRemovedSecondaryPosition: (e: ChangeEventType) => void;
  handleSecondaryPosition: (e: ChangeEventType) => void;
  handlePrimaryPosition: (e: ChangeEventType) => void;
  secondaryLocationsTieringSelection: { [key: string]: string };
  numberOfPositionsDeleted: number;
  setSecondaryLocationsTieringSelection: Dispatch<
    SetStateAction<{
      [key: string]: string;
    }>
  >;
}
export const EditJaneViewFormContext = createContext(
  {} as UseFormBehaviors<IEditJaneViewForm, EditJaneApiError> & IEditJaneViewFormActions,
);

export const EditJaneViewFormProvider: FC = ({ children }) => {
  const [{ positionId }] = useURLParameters<{ positionId?: string }>();
  const { refetch: refetchBackgroundPageData } = usePositionsAllocationSettingsPageDataContext();
  const {
    locationFilter: { data: communities },
  } = useAuthRouteDataContext();

  const { data, refetch } = useFetchJaneByIdDataContext();
  const {
    data: positionByDepartmentData,
    refetch: refetchPositionByDepartmentData,
  }: {
    data: SelectPositionByDepartmentDataType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refetch: any;
  } = useContext(SelectInputPositionByDepartmentContext);

  const _options = getPositionByDepartmentSelectOptions(positionByDepartmentData);
  const positionIdsByDepartments = mapPositionIdsByDepartment(positionByDepartmentData);
  const secondaryPositionIds = useMemo(
    () =>
      (data?.secondaryPositions || []).map(
        (item: { details: { id: number } }) => `${item.details.id}`,
      ),
    [data?.secondaryPositions],
  );

  const { apiRequest } = useAPIActions();
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false);
  const [showDeactivateConfirmModal, setShowDeactivateConfirmModal] = useState(false);
  const [numberOfPositionsDeleted, setNumberOfPositionsDeleted] = useState(0);
  const [secondaryLocationsTieringSelection, setSecondaryLocationsTieringSelection] = useState<
    IEditJaneViewFormActions['secondaryLocationsTieringSelection']
  >({});

  const handleSave = useCallback(
    async (formValues: IEditJaneViewForm) => {
      const selectedSecondaryPositionsWithoutDepartments =
        formValues.selectedSecondaryPositions.filter(
          (id) => !Object.keys(positionIdsByDepartments).includes(id),
        );
      const editJaneFormValues = {
        ...formValues,
        selectedSecondaryPositions: selectedSecondaryPositionsWithoutDepartments,
      };
      const isSameIds =
        editJaneFormValues.selectedSecondaryPositions.length === data.secondaryPositions.length &&
        editJaneFormValues.selectedSecondaryPositions.every((id: string) =>
          data.secondaryPositions.some(
            (position: { details: { displayId: number } }) =>
              position.details.displayId === Number(id),
          ),
        );
      const payload = prepareEditPayload(editJaneFormValues, data, isSameIds);
      return await apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_PATCH,
          url: B2B_JANES(data?.id),
          body: payload,
        },
        {
          onSuccess: async () => {
            if (refetch) refetch();
            if (refetchBackgroundPageData) refetchBackgroundPageData();
            toastService.success(i18next.t('team_member:PROFILE_SUCCESSFULLY_SAVED'));
            positionId
              ? removePositionModalRoutingService.open({ positionId }, 'PositionTeamMembersView')
              : janeDetailsModalRoutingService.goToPrevView();
          },
          onError: handleGenericErrorMessage,
        },
      );
    },
    [data, apiRequest, positionIdsByDepartments, refetch, refetchBackgroundPageData, positionId],
  );

  const community = data?.primaryCommunity;

  const locationOptionsTransformer = {
    name: 'location',
    label: community?.name,
    key: community?.id,
    value: community?.id,
    id: community?.id,
    type: community?.typename,
  };
  const form = useFormBehaviors<IEditJaneViewForm, EditJaneApiError>({
    isDirtyCheckEnabled: true,
    initialState: {
      position_id: [`${data?.primaryPosition?.details?.id}`],
      location: locationOptionsTransformer,
      selectedSecondaryPositions: [
        ...secondaryPositionIds,
        ...Object.entries(positionIdsByDepartments)
          .filter(([_, positionIds]) => {
            const filteredPositionIds = positionIds.filter(
              (id) => id !== `${data?.primaryPosition?.details?.id}`,
            );
            return filteredPositionIds.every((id) => secondaryPositionIds.includes(id));
          })
          .map(([department]) => department),
      ],
    },
    onSubmit: handleSave,
    validations: {
      position_id: VALIDATION_position_id,
    },
  });

  const handlePrimaryLocationChange = useCallback(
    async (e) => {
      await refetchPositionByDepartmentData({
        filter: { allocatedToCommunities: [Number(e.target.value?.value)] },
      });

      return form.onChange([
        e,
        {
          target: {
            name: 'position_id',
            value: [],
          },
        },
        {
          target: {
            name: 'selectedSecondaryPositions',
            value: [],
          },
        },
      ]);
    },
    [form, refetchPositionByDepartmentData],
  );

  const { handlePrimaryPosition, handleSecondaryPosition, handleRemovedSecondaryPosition } =
    useJanePositionDropdownHandlers({
      onChange: form.onChange,
      positionIdsByDepartments,
      primaryPositionId: form.values.position_id[0],
      secondaryPositionIds: form.values.selectedSecondaryPositions,
      secondaryPositionsFormName: 'selectedSecondaryPositions',
      setShowDeactivateConfirmModal,
      setNumberOfPositionsDeleted,
    });
  const secondaryPositionsDropdownItems = useMemo(
    () =>
      _options &&
      _options
        .filter((item) => `${item.value}` !== `${form.values.position_id[0]}`)
        .filter((item) => {
          // if the department is the only node in this group, filter it out
          const isDepartmentOnlyNodeInGroup =
            item.isGroupNode &&
            positionByDepartmentData[item.groupId]?.filter(
              (positionIdByDepartmentNode) =>
                `${positionIdByDepartmentNode?.value}` !== `${form.values.position_id[0]}`,
            ).length < 1;

          return !isDepartmentOnlyNodeInGroup;
        })
        .map(
          (item) =>
            ({
              id: `${item?.key}`,
              value: item?.value,
              label: item?.label,
              key: `${item?.key}`,
              groupId: `${item?.groupId}`,
              isGroupNode: !!item?.isGroupNode,
            } as SecondaryPositionDropdownItem),
        ),
    [_options, form.values.position_id, positionByDepartmentData],
  );

  const value = useMemo(() => {
    const primaryCommunityId = data?.primaryCommunity?.id;
    const isEditable = communities.some((community) => community.id === primaryCommunityId);
    return {
      ...form,
      isDisabled: !form.values.position_id || !isEditable,
      secondaryPositionsDropdownItems,
      showDeactivateConfirmModal,
      setShowDeactivateConfirmModal,
      isDiscardConfirmationOpen,
      setIsDiscardConfirmationOpen,
      handleRemovedSecondaryPosition,
      handleSecondaryPosition,
      handlePrimaryPosition,
      handlePrimaryLocationChange,
      numberOfPositionsDeleted,
      secondaryLocationsTieringSelection,
      setSecondaryLocationsTieringSelection,
    };
  }, [
    data?.primaryCommunity?.id,
    communities,
    form,
    secondaryPositionsDropdownItems,
    showDeactivateConfirmModal,
    isDiscardConfirmationOpen,
    handleRemovedSecondaryPosition,
    handleSecondaryPosition,
    handlePrimaryPosition,
    handlePrimaryLocationChange,
    numberOfPositionsDeleted,
    secondaryLocationsTieringSelection,
  ]);

  return (
    <EditJaneViewFormContext.Provider value={value}>{children}</EditJaneViewFormContext.Provider>
  );
};

export const useEditJaneViewFormContext = () => useContext(EditJaneViewFormContext);
